// ============================================================================
// AdminRegistrations — registrations management panel for the admin app.
// ============================================================================
// Replaces the old AdminPanel's per-event date tabs with a searchable event
// dropdown. The dropdown can show all events (default) or filter to one event
// at a time. Per-event seat counts and registration totals are still surfaced.
//
// Wired into AdminLayout via MainApp's #admin/registrations route.
// ============================================================================

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Mail, Phone, Users, Search, RefreshCw, Send, CheckCircle2, UserPlus, Pencil, ArrowUp, ArrowDown, QrCode, Printer } from 'lucide-react';
import { api, type AdminRegistration, type PublicEvent } from '../api';
import { EmailBlastModal } from './EmailBlastModal';
import { EditRegistrantModal } from './EditRegistrantModal';
import { QrCodeModal } from './QrCodeModal';

type SortKey = 'name' | 'event' | 'seats' | 'status' | 'confirmed' | 'registered';
type SortDir = 'asc' | 'desc';

// Status sort key for the StatusBadge column: registered first, then
// attended, waitlisted, cancelled, no_show. Matches admin mental model
// (active registrations are the focus).
const STATUS_ORDER: Record<string, number> = {
  registered: 0, attended: 1, waitlisted: 2, cancelled: 3, no_show: 4,
};

interface Props {
  token: string;
  onLogout: () => void;
  /** Optional event_id from the URL query (`?event=123`) to pre-filter. */
  initialEventId?: number | null;
  onRegistrationsCount?: (count: number) => void;
}

export function AdminRegistrations({ token, onLogout, initialEventId, onRegistrationsCount }: Props) {
  const [registrations, setRegistrations] = useState<AdminRegistration[]>([]);
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [activeEventId, setActiveEventId] = useState<number | null>(initialEventId ?? null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  // Task #49 + #50: per-day attendance map for the currently-selected event.
  // Key format: `${attendee_id}|${guest_id || ''}|${day_index}` → present.
  // guest_id empty string = the primary attendee on that row.
  const [attendance, setAttendance] = useState<Map<string, boolean>>(new Map());
  const [attendanceBusy, setAttendanceBusy] = useState<string | null>(null);
  // Track which attendee row has the "+ Add guest" inline editor open.
  const [addGuestOpen, setAddGuestOpen] = useState<number | null>(null);
  const [newGuestName, setNewGuestName] = useState('');
  // Task #51: which registrant is being edited.
  const [editingRegistrant, setEditingRegistrant] = useState<AdminRegistration | null>(null);
  // Task #52: sign-in QR modal state — null when closed, else {dayIndex}.
  const [signinQrDay, setSigninQrDay] = useState<number | null>(null);
  // Task #1 (email enhancements): selected attendee_ids for surgical
  // emails. When non-empty, "Email selected (N)" replaces the bulk-mode
  // header button and pre-loads the composer with this list.
  const [selectedAttendeeIds, setSelectedAttendeeIds] = useState<Set<number>>(new Set());
  // Default sort: most recent registrations first — matches the "what's new
  // overnight?" workflow when checking inbound signups.
  const [sortKey, setSortKey] = useState<SortKey>('registered');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [evRes, regRes] = await Promise.all([
        api.events({ includePast: true }),
        api.adminRegistrations(token),
      ]);
      const sorted = [...evRes.events].sort(
        (a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
      );
      setEvents(sorted);
      setRegistrations(regRes.registrations);
      onRegistrationsCount?.(regRes.registrations.filter((r) => r.registration_status === 'registered').length);
    } catch (err: any) {
      if (err?.message?.toLowerCase().includes('unauthorized')) {
        onLogout();
        return;
      }
      setError(err?.message || 'Failed to load registrations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleStatusChange = async (attendeeId: number, status: string) => {
    setUpdatingId(attendeeId);
    try {
      await api.adminUpdateRegistrationStatus(token, attendeeId, status);
      setRegistrations((prev) =>
        prev.map((r) => (r.attendee_id === attendeeId ? { ...r, registration_status: status } : r))
      );
    } catch (err: any) {
      alert(err?.message || 'Failed to update status.');
    } finally {
      setUpdatingId(null);
    }
  };

  // ----- Filtering -----
  const eventFiltered = activeEventId == null
    ? registrations
    : registrations.filter((r) => r.event_id === activeEventId);

  const searchFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return eventFiltered;
    return eventFiltered.filter((r) =>
      r.full_name.toLowerCase().includes(q)
      || r.email.toLowerCase().includes(q)
      || (r.event_title || '').toLowerCase().includes(q)
    );
  }, [eventFiltered, search]);

  const sorted = useMemo(() => {
    const list = [...searchFiltered];
    const cmp = (a: AdminRegistration, b: AdminRegistration): number => {
      let av: string | number = '';
      let bv: string | number = '';
      switch (sortKey) {
        case 'name':
          av = a.full_name.toLowerCase(); bv = b.full_name.toLowerCase(); break;
        case 'event':
          av = (a.event_title || '').toLowerCase(); bv = (b.event_title || '').toLowerCase(); break;
        case 'seats':
          av = a.attendee_count || 0; bv = b.attendee_count || 0; break;
        case 'status':
          av = STATUS_ORDER[a.registration_status] ?? 99;
          bv = STATUS_ORDER[b.registration_status] ?? 99;
          break;
        case 'confirmed':
          // Sort confirmed (newest first) before unconfirmed.
          av = a.confirmed_at ? -new Date(a.confirmed_at).getTime() : 1;
          bv = b.confirmed_at ? -new Date(b.confirmed_at).getTime() : 1;
          break;
        case 'registered':
          av = new Date(a.created_at).getTime(); bv = new Date(b.created_at).getTime(); break;
      }
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    };
    list.sort(cmp);
    if (sortDir === 'desc') list.reverse();
    return list;
  }, [searchFiltered, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      // Date-ish defaults to desc; other columns to asc.
      setSortDir(key === 'registered' ? 'desc' : 'asc');
    }
  };

  // ----- Stats for the visible scope -----
  const SEAT_OCCUPYING = new Set(['registered', 'attended']);
  const totalAttendees = eventFiltered
    .filter((r) => SEAT_OCCUPYING.has(r.registration_status))
    .reduce((s, r) => s + (r.attendee_count || 1), 0);
  const registeredCount = eventFiltered.filter((r) => r.registration_status === 'registered').length;
  const waitlistCount   = eventFiltered.filter((r) => r.registration_status === 'waitlisted').length;
  const cancelledCount  = eventFiltered.filter((r) => r.registration_status === 'cancelled').length;

  const selectedEvent = activeEventId == null ? null : events.find((e) => e.event_id === activeEventId);
  // Day count for the selected event (1 for single-day, N for multi-day).
  const dayCount = useMemo(() => {
    if (!selectedEvent) return 1;
    return Math.max(1, selectedEvent.days?.length ?? 1);
  }, [selectedEvent]);

  // Load attendance whenever the selected event changes.
  const loadAttendance = useCallback(async () => {
    if (!selectedEvent) { setAttendance(new Map()); return; }
    try {
      const d = await api.listAttendance(token, selectedEvent.calendar_entry_id);
      const map = new Map<string, boolean>();
      for (const row of d.attendance) {
        const key = `${row.attendee_id}|${row.guest_id ?? ''}|${row.day_index}`;
        map.set(key, row.present);
      }
      setAttendance(map);
    } catch {
      // Non-fatal — admin can still see registrations even if attendance is unreachable.
      setAttendance(new Map());
    }
  }, [selectedEvent, token]);

  useEffect(() => { loadAttendance(); }, [loadAttendance]);

  // Clear the selection set whenever the event filter changes — selection
  // is scoped to the currently-visible event so cross-event picks would be
  // confusing.
  useEffect(() => { setSelectedAttendeeIds(new Set()); }, [activeEventId]);

  const toggleSelect = (attendeeId: number) => {
    setSelectedAttendeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(attendeeId)) next.delete(attendeeId);
      else next.add(attendeeId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = sorted.map((r) => r.attendee_id);
    const allOn = visibleIds.every((id) => selectedAttendeeIds.has(id));
    setSelectedAttendeeIds(allOn ? new Set() : new Set(visibleIds));
  };

  const togglePresent = async (attendeeId: number, dayIndex: number, guestId: number | null) => {
    const key = `${attendeeId}|${guestId ?? ''}|${dayIndex}`;
    const current = attendance.get(key) ?? false;
    const next = !current;
    setAttendanceBusy(key);
    try {
      await api.markAttendance(token, attendeeId, {
        day_index: dayIndex,
        present: next,
        check_in_method: 'manual',
        guest_id: guestId ?? undefined,
      });
      setAttendance((prev) => {
        const m = new Map(prev);
        m.set(key, next);
        return m;
      });
      // Refresh to pick up status sync for primary attendee toggles.
      if (guestId == null) await load();
    } catch (err: any) {
      alert(err?.message || 'Could not mark attendance.');
    } finally {
      setAttendanceBusy(null);
    }
  };

  const saveNewGuest = async (attendeeId: number) => {
    const name = newGuestName.trim();
    if (!name) return;
    try {
      await api.addGuest(token, attendeeId, name);
      setNewGuestName('');
      setAddGuestOpen(null);
      await load();
    } catch (err: any) {
      alert(err?.message || 'Could not add guest.');
    }
  };

  const removeGuest = async (attendeeId: number, guestId: number, name: string) => {
    if (!window.confirm(`Remove guest "${name}" from this registration?`)) return;
    try {
      await api.archiveGuest(token, attendeeId, guestId);
      await load();
    } catch (err: any) {
      alert(err?.message || 'Could not remove guest.');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Registrations</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            {selectedEvent
              ? <>Viewing <span className="font-medium">{selectedEvent.title}</span></>
              : 'Viewing all events'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedEvent && (
            <>
              <button
                type="button"
                onClick={() => setSigninQrDay(1)}
                className="inline-flex items-center gap-1.5 rounded-md border border-uif-green/40 bg-white px-3 py-1.5 text-sm font-medium text-uif-green-dark hover:bg-uif-green/5"
                title="Show the QR code attendees scan at the venue to sign in"
              >
                <QrCode className="h-4 w-4" />
                Sign-in QR{dayCount > 1 ? ' (Day 1)' : ''}
              </button>
              {dayCount > 1 && Array.from({ length: dayCount - 1 }, (_, i) => i + 2).map((d) => (
                <button
                  key={`qr-day-${d}`}
                  type="button"
                  onClick={() => setSigninQrDay(d)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-uif-green/40 bg-white px-3 py-1.5 text-sm font-medium text-uif-green-dark hover:bg-uif-green/5"
                  title={`Sign-in QR for Day ${d}`}
                >
                  <QrCode className="h-4 w-4" />
                  Day {d}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setEmailModalOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark"
                title="Send a bulk email to this event's registrants"
              >
                <Send className="h-4 w-4" />
                Email registrants
              </button>
              {/* Task #44: the report route is keyed by event_id, not the
                  calendar_entry_id that loadAttendance above passes. */}
              <button
                type="button"
                onClick={() => { window.location.hash = `#admin/events/${selectedEvent.event_id}/attendance-report`; }}
                className="inline-flex items-center gap-1.5 rounded-md border border-uif-green/40 bg-white px-3 py-1.5 text-sm font-medium text-uif-green-dark hover:bg-uif-green/5"
                title="Open the printable attendance report for this event"
              >
                <Printer className="h-4 w-4" />
                Print attendance report
              </button>
            </>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <label className="text-xs font-medium text-gray-700 flex-shrink-0">Event:</label>
          <select
            value={activeEventId ?? ''}
            onChange={(e) => setActiveEventId(e.target.value ? Number(e.target.value) : null)}
            className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="">All events</option>
            {events.map((e) => (
              <option key={e.event_id} value={e.event_id}>
                {formatEventOption(e)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[180px] relative">
          <Search className="h-4 w-4 text-gray-400 absolute left-2 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, or event…"
            className="flex-1 rounded-md border border-gray-300 pl-8 pr-3 py-1.5 text-sm"
          />
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Registrations" value={registeredCount} />
        <StatCard label="Total attendees" value={totalAttendees} icon={<Users className="h-4 w-4" />} />
        <StatCard label="Waitlisted" value={waitlistCount} />
        <StatCard label="Cancelled" value={cancelledCount} />
      </div>

      {/* Task #1: surgical-email action bar when selection is non-empty. */}
      {selectedEvent && selectedAttendeeIds.size > 0 && (
        <div className="bg-uif-green/10 border border-uif-green/30 rounded-lg p-3 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-uif-green-dark font-medium">
            {selectedAttendeeIds.size} selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEmailModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark"
            >
              <Send className="h-4 w-4" /> Email selected ({selectedAttendeeIds.size})
            </button>
            <button
              type="button"
              onClick={() => setSelectedAttendeeIds(new Set())}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {selectedEvent && (
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all visible"
                    checked={sorted.length > 0 && sorted.every((r) => selectedAttendeeIds.has(r.attendee_id))}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 accent-uif-green"
                  />
                </th>
              )}
              <SortableTh label="Name"       activeKey={sortKey} dir={sortDir} sortKey="name"       onClick={toggleSort} />
              <SortableTh label="Event"      activeKey={sortKey} dir={sortDir} sortKey="event"      onClick={toggleSort} />
              <Th>Contact</Th>
              <SortableTh label="Seats"      activeKey={sortKey} dir={sortDir} sortKey="seats"      onClick={toggleSort} />
              <SortableTh label="Status"     activeKey={sortKey} dir={sortDir} sortKey="status"     onClick={toggleSort} />
              <SortableTh label="Confirmed"  activeKey={sortKey} dir={sortDir} sortKey="confirmed"  onClick={toggleSort} />
              {selectedEvent && Array.from({ length: dayCount }, (_, i) => (
                <th key={`day-h-${i + 1}`} className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {dayCount === 1 ? 'Attended' : `Day ${i + 1}`}
                </th>
              ))}
              <SortableTh label="Registered" activeKey={sortKey} dir={sortDir} sortKey="registered" onClick={toggleSort} />
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading && (
              <tr><td colSpan={selectedEvent ? 9 + dayCount : 8} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr>
                <td colSpan={selectedEvent ? 9 + dayCount : 8} className="px-4 py-8 text-center text-gray-500">
                  {registrations.length === 0 ? 'No registrations yet.' : 'No matches for this filter.'}
                </td>
              </tr>
            )}
            {sorted.map((r) => {
              const guests = r.guests ?? [];
              const colsAfterDays = 2; // Registered + Actions
              return (
              <Fragment key={r.attendee_id}>
              <tr className={'hover:bg-gray-50 ' + (selectedAttendeeIds.has(r.attendee_id) ? 'bg-uif-green/5' : '')}>
                {selectedEvent && (
                  <td className="px-3 py-3 align-top">
                    <input
                      type="checkbox"
                      checked={selectedAttendeeIds.has(r.attendee_id)}
                      onChange={() => toggleSelect(r.attendee_id)}
                      aria-label={`Select ${r.full_name}`}
                      className="h-4 w-4 accent-uif-green"
                    />
                  </td>
                )}
                <Td>
                  <div className="font-medium text-gray-900">
                    {r.full_name}
                    {r.attendee_count > 1 && (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wider font-semibold text-gray-500">
                        primary
                      </span>
                    )}
                  </div>
                  {r.special_requests && (
                    <div className="text-xs text-gray-500 mt-0.5">{r.special_requests}</div>
                  )}
                </Td>
                <Td>
                  <div className="text-xs text-gray-700">{r.event_title}</div>
                </Td>
                <Td>
                  <div className="flex items-center gap-1 text-xs text-gray-700">
                    <Mail className="h-3 w-3" />
                    <a href={`mailto:${r.email}`} className="hover:underline text-uif-green">{r.email}</a>
                  </div>
                  {r.phone && (
                    <div className="flex items-center gap-1 text-xs text-gray-700 mt-0.5">
                      <Phone className="h-3 w-3" />
                      <a href={`tel:${r.phone}`} className="hover:underline">{r.phone}</a>
                    </div>
                  )}
                </Td>
                <Td>{r.attendee_count}</Td>
                <Td><StatusBadge status={r.registration_status} /></Td>
                <Td>
                  {r.confirmed_at ? (
                    <span className="inline-flex items-center gap-1 text-xs text-uif-green-dark">
                      <CheckCircle2 className="h-3 w-3" />
                      {new Date(r.confirmed_at).toLocaleDateString()}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </Td>
                {selectedEvent && Array.from({ length: dayCount }, (_, i) => {
                  const di = i + 1;
                  const key = `${r.attendee_id}||${di}`;
                  const present = attendance.get(key) ?? false;
                  return (
                    <td key={`day-${r.attendee_id}-${di}`} className="px-3 py-3 text-center align-top">
                      <input
                        type="checkbox"
                        checked={present}
                        onChange={() => togglePresent(r.attendee_id, di, null)}
                        disabled={attendanceBusy === key}
                        className="h-4 w-4 accent-uif-green"
                        aria-label={`Mark ${r.full_name} present on day ${di}`}
                      />
                    </td>
                  );
                })}
                <Td>
                  <span className="text-xs text-gray-600">
                    {new Date(r.created_at).toLocaleDateString()}
                  </span>
                </Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <select
                      value={r.registration_status}
                      onChange={(e) => handleStatusChange(r.attendee_id, e.target.value)}
                      disabled={updatingId === r.attendee_id}
                      className="text-xs rounded border border-gray-300 px-2 py-1 bg-white"
                    >
                      <option value="registered">Registered</option>
                      <option value="waitlisted">Waitlisted</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="attended">Attended</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => setEditingRegistrant(r)}
                      className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-uif-green-dark"
                      title="Edit name, email, or phone"
                    >
                      <Pencil className="h-3 w-3" />
                      Edit
                    </button>
                  </div>
                </Td>
              </tr>

              {/* Task #50: guest sub-rows. Each guest gets their own row with
                  per-day attendance checkboxes. */}
              {selectedEvent && guests.map((g) => (
                <tr key={`g-${g.guest_id}`} className="bg-gray-50/60 hover:bg-gray-100">
                  <td className="px-3 py-2 align-top" />
                  <Td>
                    <div className="pl-4 text-xs flex items-center gap-2">
                      <span className="text-gray-400">↳</span>
                      <span className="text-gray-900">{g.full_name}</span>
                      <button
                        type="button"
                        onClick={() => removeGuest(r.attendee_id, g.guest_id, g.full_name)}
                        className="text-[10px] text-red-700 hover:underline"
                        title="Remove this guest"
                      >
                        remove
                      </button>
                    </div>
                  </Td>
                  <td colSpan={5} className="px-3 py-2 text-[11px] text-gray-500 italic">
                    Guest of {r.full_name}
                  </td>
                  {Array.from({ length: dayCount }, (_, i) => {
                    const di = i + 1;
                    const key = `${r.attendee_id}|${g.guest_id}|${di}`;
                    const present = attendance.get(key) ?? false;
                    return (
                      <td key={`gd-${g.guest_id}-${di}`} className="px-3 py-2 text-center align-middle">
                        <input
                          type="checkbox"
                          checked={present}
                          onChange={() => togglePresent(r.attendee_id, di, g.guest_id)}
                          disabled={attendanceBusy === key}
                          className="h-4 w-4 accent-uif-green"
                          aria-label={`Mark guest ${g.full_name} present on day ${di}`}
                        />
                      </td>
                    );
                  })}
                  <td colSpan={colsAfterDays} />
                </tr>
              ))}

              {/* "+ Add guest" row, shown only when seats > guests.length + 1 */}
              {selectedEvent && r.attendee_count > guests.length + 1 && (
                <tr className="bg-gray-50/40">
                  <td colSpan={selectedEvent ? 9 + dayCount : 8} className="px-4 py-1.5">
                    {addGuestOpen === r.attendee_id ? (
                      <div className="flex items-center gap-2 pl-4">
                        <input
                          type="text"
                          value={newGuestName}
                          onChange={(e) => setNewGuestName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveNewGuest(r.attendee_id); }}
                          placeholder="Guest name"
                          autoFocus
                          className="flex-1 max-w-xs rounded-md border border-gray-300 px-2 py-1 text-xs"
                        />
                        <button
                          type="button"
                          onClick={() => saveNewGuest(r.attendee_id)}
                          className="text-xs bg-uif-green text-white font-semibold px-3 py-1 rounded-md hover:bg-uif-green-dark"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => { setAddGuestOpen(null); setNewGuestName(''); }}
                          className="text-xs text-gray-600 hover:text-gray-900"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => { setAddGuestOpen(r.attendee_id); setNewGuestName(''); }}
                        className="pl-4 text-xs text-gray-600 hover:text-uif-green-dark inline-flex items-center gap-1"
                      >
                        <UserPlus className="h-3 w-3" />
                        Add guest name ({r.attendee_count - 1 - guests.length} unnamed)
                      </button>
                    )}
                  </td>
                </tr>
              )}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedEvent && (
        <EmailBlastModal
          open={emailModalOpen}
          onClose={() => { setEmailModalOpen(false); load(); }}
          token={token}
          calendarEntryId={selectedEvent.calendar_entry_id}
          eventTitle={selectedEvent.title}
          counts={{
            registered: eventFiltered.filter((r) => r.registration_status === 'registered').length,
            waitlisted: eventFiltered.filter((r) => r.registration_status === 'waitlisted').length,
            registered_unconfirmed: eventFiltered.filter((r) => r.registration_status === 'registered' && !r.confirmed_at).length,
            registered_confirmed:   eventFiltered.filter((r) => r.registration_status === 'registered' && !!r.confirmed_at).length,
          }}
          selectedAttendeeIds={selectedAttendeeIds.size > 0 ? Array.from(selectedAttendeeIds) : undefined}
          selectionLabel={selectedAttendeeIds.size > 0 ? `Picked from the Registrations table on ${selectedEvent.title}.` : undefined}
        />
      )}

      <EditRegistrantModal
        open={editingRegistrant != null}
        onClose={() => setEditingRegistrant(null)}
        onSaved={() => load()}
        token={token}
        registrant={editingRegistrant}
      />

      {selectedEvent && signinQrDay != null && (
        <QrCodeModal
          open={true}
          onClose={() => setSigninQrDay(null)}
          url={`${window.location.origin}/#signin/${selectedEvent.event_id}/${signinQrDay}`}
          title={`Sign-in QR · ${selectedEvent.title}${dayCount > 1 ? ` · Day ${signinQrDay}` : ''}`}
          filename={`signin-qr-${selectedEvent.slug || selectedEvent.event_id}-day-${signinQrDay}`}
        />
      )}
    </div>
  );
}

function formatEventOption(e: PublicEvent): string {
  const d = new Date(e.start_date);
  const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  return `${dateStr} — ${e.title}`;
}

function StatCard({ label, value, icon }: { label: string; value: number; icon?: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="text-xs uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
        {icon}{label}
      </div>
      <div className="text-2xl font-bold text-uif-green-dark">{value}</div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{children}</th>;
}

function SortableTh({ label, activeKey, dir, sortKey, onClick }: {
  label: string; activeKey: SortKey; dir: SortDir; sortKey: SortKey; onClick: (k: SortKey) => void;
}) {
  const active = activeKey === sortKey;
  return (
    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={
          'inline-flex items-center gap-1 select-none ' +
          (active ? 'text-uif-green-dark' : 'text-gray-600 hover:text-gray-900')
        }
        aria-label={`Sort by ${label}`}
      >
        {label}
        {active && (dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3 align-top">{children}</td>;
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    registered: 'bg-uif-green/10 text-uif-green-dark',
    waitlisted: 'bg-yellow-100 text-yellow-800',
    cancelled:  'bg-red-100 text-red-800',
    attended:   'bg-blue-100 text-blue-800',
    no_show:    'bg-gray-100 text-gray-700',
  };
  const cls = styles[status] ?? 'bg-gray-100 text-gray-700';
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{status}</span>;
}
